import produce from 'immer'
import { combineReducers } from 'redux'
import {
  banAsync,
  fetchBansAsync,
  fetchEndUserLastViewedPageAsync,
  fetchEndUsersAsync,
  recievedUserStartedTyping,
  removeOldPresence,
  unbanAsync,
  updateCQLAsync,
  updatePresence,
} from 'shared/store/actions/endUser'
import Page from 'shared/typings/context/Page'
import { EndUser } from 'shared/typings/endUser/EndUser'

import { AnyRootAction, createReducer } from 'typesafe-actions'

const MAX_PRESENCE_RECORD_AGE = 30000

type EndUsersById = Record<string, EndUser | undefined>

type EndUsersPresenceById = Record<string, number>

type EndUserPageById = Record<string, Page>

export interface BansState {
  ips: string[]
  endUserIds: string[]
}

const bans = createReducer<BansState, AnyRootAction>({ ips: [], endUserIds: [] })
  .handleAction([fetchBansAsync.success], (_, action) => {
    return {
      ips: action.payload.bannedIps,
      endUserIds: action.payload.bannedEndUserIds || [],
    }
  })
  .handleAction([banAsync.success], (state, action) => {
    return produce(state, (draft) => {
      draft.ips = [...draft.ips, action.payload.ip]
      draft.endUserIds = [...draft.endUserIds, action.payload.endUserId]
    })
  })
  .handleAction([unbanAsync.success], (state, action) => {
    return produce(state, (draft) => {
      draft.ips = draft.ips.filter((ip) => ip !== action.payload.ip)
      draft.endUserIds = draft.endUserIds.filter((id) => id !== action.payload.endUserId)
    })
  })

const byId = createReducer<EndUsersById, AnyRootAction>({})
  .handleAction([fetchEndUsersAsync.success], (state, action) => {
    return produce(state, (draft) => {
      action.payload.forEach((endUser: EndUser) => {
        draft[endUser.id] = endUser
      })
    })
  })
  .handleAction(updateCQLAsync.success, (state, action) => {
    const endUser = state[action.payload.endUserId]
    if (!endUser) {
      return state
    }

    const newUser = produce(endUser, (draft) => {
      draft.attributes._cql_score = action.payload.score
    })

    return produce(state, (draft) => {
      draft[action.payload.endUserId] = newUser
    })
  })

const presenceById = createReducer<EndUsersPresenceById>({})
  .handleAction(recievedUserStartedTyping, (state, action) => {
    const { payload } = action
    return produce(state, (draft) => {
      draft[payload.userId] = Date.now()
    })
  })
  .handleAction(updatePresence, (state, action) => {
    const { payload } = action
    return produce(state, (draft) => {
      draft[payload.id] = payload.timestamp
    })
  })
  .handleAction(removeOldPresence, (state) =>
    /*
    this runs every five seconds. be careful what you do in here as it could cause
    many rerenders.
  */
    produce(state, (draft) => {
      Object.keys(draft).forEach((item) => {
        if (draft[Number(item)] < Date.now() - MAX_PRESENCE_RECORD_AGE) {
          delete draft[Number(item)]
        }
      })
    })
  )

const pageById = createReducer<EndUserPageById>({})
  .handleAction(updatePresence, (state, action) => {
    const { payload } = action
    if (!payload.page) {
      return state
    }
    return produce(state, (draft) => {
      draft[payload.id] = payload.page
    })
  })
  .handleAction(fetchEndUserLastViewedPageAsync.success, (state, action) => {
    const { payload } = action

    return produce(state, (draft) => {
      if (payload.length > 0) {
        const lastViewedPageAndEndUser = payload[0]
        const endUserId = lastViewedPageAndEndUser.id
        draft[endUserId] = lastViewedPageAndEndUser.page
      }
    })
  })

export default combineReducers({
  byId,
  presenceById,
  bans,
  pageById,
})
